import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authLogin, authLogout, authMe, authRegister } from "../api/authApi";
import { accountPreviewEnabled, clearAccountPreview, PREVIEW_ACCOUNT } from "../dev/preview";

interface AccountAuthState {
  accessToken: string | null;
  sub: string | null;
  email: string | null;
  createdAt: number | null;
  loggedIn: boolean;
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ requiresActivation: boolean }>;
  logout: () => Promise<void>;
  setAccessToken: (token: string | null) => void;
}

function createdFrom(createdAt: number | null | undefined): number | null {
  return createdAt && createdAt > 0 ? createdAt : null;
}

const AccountAuthContext = createContext<AccountAuthState | null>(null);

export function AccountAuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<{
    accessToken: string | null;
    sub: string;
    email: string;
    createdAt: number | null;
  } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (accountPreviewEnabled()) {
      setSession({
        accessToken: "preview",
        sub: PREVIEW_ACCOUNT.sub,
        email: PREVIEW_ACCOUNT.email,
        createdAt: PREVIEW_ACCOUNT.createdAt,
      });
      setReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const me = await authMe();
        if (cancelled) return;
        if (me) {
          setSession({
            accessToken: null,
            sub: me.sub,
            email: me.email ?? "",
            createdAt: createdFrom(me.created_at),
          });
        } else {
          setSession(null);
        }
      } catch {
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authLogin(email.trim().toLowerCase(), password);
    const me = await authMe();
    setSession({
      accessToken: result.access_token,
      sub: me?.sub ?? result.sub,
      email: me?.email ?? email.trim().toLowerCase(),
      createdAt: createdFrom(me?.created_at),
    });
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const result = await authRegister(email.trim().toLowerCase(), password);
    if (result.access_token) {
      const me = await authMe();
      setSession({
        accessToken: result.access_token,
        sub: me?.sub ?? result.sub,
        email: me?.email ?? email.trim().toLowerCase(),
        createdAt: createdFrom(me?.created_at),
      });
    }
    return { requiresActivation: Boolean(result.requires_activation) };
  }, []);

  const logout = useCallback(async () => {
    const preview = accountPreviewEnabled();
    clearAccountPreview();
    if (preview) {
      setSession(null);
      return;
    }
    try {
      await authLogout();
    } finally {
      setSession(null);
    }
  }, []);

  const setAccessToken = useCallback((token: string | null) => {
    setSession((prev) => (prev ? { ...prev, accessToken: token } : prev));
  }, []);

  const value = useMemo<AccountAuthState>(
    () => ({
      accessToken: session?.accessToken ?? null,
      sub: session?.sub ?? null,
      email: session?.email ?? null,
      createdAt: session?.createdAt ?? null,
      loggedIn: session !== null,
      ready,
      login,
      register,
      logout,
      setAccessToken,
    }),
    [session, ready, login, register, logout, setAccessToken],
  );

  return <AccountAuthContext.Provider value={value}>{children}</AccountAuthContext.Provider>;
}

export function useAccountAuth(): AccountAuthState {
  const ctx = useContext(AccountAuthContext);
  if (!ctx) throw new Error("useAccountAuth outside AccountAuthProvider");
  return ctx;
}
